import { isDefined, isNonEmpty } from '@sgme/fp';
import { useReadonlyField } from 'components/common-form/contexts/readonly/FieldMetadataContext';
import { SummaryFieldRow } from 'components/common-form/sidePanel/docToolbar/summary/builder/node/SummaryFieldRow';
import { FormattedMessage } from 'components/shared/Intl';
import type { IntlKey } from 'locales';
import { isObject } from 'lodash-es';
import { AdditionalCalendarsReadonlyValueDefinition } from 'models/metadata/readonlyValue.definition';
import { JSX, useMemo } from 'react';
import { useIntl } from 'react-intl';

interface ProductSummaryAdditionalCalendarsFieldProps {
  node: AdditionalCalendarsReadonlyValueDefinition;
}
const EMPTY_DATA = [] as const;
export function ProductSummaryAdditionalCalendarsField({
  node,
}: ProductSummaryAdditionalCalendarsFieldProps): JSX.Element {
  const intl = useIntl();

  const data = useReadonlyField().getMetadataWithData(node) ?? EMPTY_DATA;

  const translationKey = node.translationKey ?? node.id;

  const hasValue = isNonEmpty(data);

  const dataToDisplay = useMemo(() => {
    const calendars = data.map(v => (isObject(v) ? v['Calendar'] : v));

    return (
      isDefined(node.translations) ? calendars.map(calendar => node.translations?.[calendar] ?? calendar) : calendars
    )
      .filter(isDefined)
      .map(calendar =>
        intl.formatMessage({
          id: `calendar.${calendar}` as IntlKey,
          defaultMessage: calendar,
        }),
      );
  }, [intl, node.translations, data]);

  return (
    <SummaryFieldRow labelKey={translationKey} id={node.id}>
      <p className="text-large fw-light m-0" data-e2e={`product-summary-field-${node.id}`}>
        {hasValue ? (
          dataToDisplay
            .map(display =>
              intl.formatMessage({
                id: `form.${display}` as IntlKey,
                defaultMessage: display,
              }),
            )
            .join(', ')
        ) : (
          <FormattedMessage id="field.None" defaultMessage="None" />
        )}
      </p>
    </SummaryFieldRow>
  );
}

ProductSummaryAdditionalCalendarsField.displayName = 'ProductSummaryAdditionalCalendarsField';
